import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Upload, FileText, CheckCircle2, AlertCircle, Clock, Plus, Trash2, Loader2, BarChart3, FileBarChart, FileArchive, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { validateImageQuality, validateZipFile } from '../utils/imageValidation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function MarkingProjectDetail() {
  const { project_id } = useParams();
  const navigate = useNavigate();
  
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [selectedSubmissions, setSelectedSubmissions] = useState([]);
  const [showQualityModal, setShowQualityModal] = useState(false);
  const [qualityIssues, setQualityIssues] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  
  // Answer Key State
  const [answerKey, setAnswerKey] = useState([]);
  const [editingAnswerKey, setEditingAnswerKey] = useState(false);
  const [extractedAnswers, setExtractedAnswers] = useState([]);
  const [answerKeyStatus, setAnswerKeyStatus] = useState('not_uploaded'); // not_uploaded, uploading, extracting, pending_review, confirmed
  const [uploadingAnswerKey, setUploadingAnswerKey] = useState(false);

  const fetchProject = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/marking/projects/${project_id}`, { withCredentials: true });
      setProject(response.data);
      setAnswerKey(response.data.answer_key || []);
      setExtractedAnswers(response.data.answer_key_extracted || []);
      setAnswerKeyStatus(response.data.answer_key_status || 'not_uploaded');
    } catch (error) {
      console.error('Error fetching project:', error);
      toast.error('Failed to load project');
      navigate('/assessment');
    } finally {
      setLoading(false);
    }
  }, [project_id, navigate]);

  const fetchProcessingStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/marking/projects/${project_id}/status`, { withCredentials: true });
      if (project) {
        setProject({ ...project, submissions: response.data.submissions });
      }
    } catch (error) {
      console.error('Error fetching status:', error);
    }
  }, [project_id, project]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (activeTab === 'papers') {
        fetchProcessingStatus();
      }
    }, 3000); // Poll every 3 seconds
    
    return () => clearInterval(interval);
  }, [activeTab, fetchProcessingStatus]);

  const addQuestion = () => {
    const newQuestion = {
      question_number: answerKey.length + 1,
      question_text: '',
      correct_answer: '',
      alternative_answers: [],
      max_marks: 1,
      partial_mark_rules: '',
      marking_instructions: ''
    };
    setAnswerKey([...answerKey, newQuestion]);
  };

  const updateQuestion = (index, field, value) => {
    const updated = [...answerKey];
    updated[index][field] = value;
    setAnswerKey(updated);
  };

  const removeQuestion = (index) => {
    setAnswerKey(answerKey.filter((_, i) => i !== index));
    // Renumber questions
    const renumbered = answerKey.filter((_, i) => i !== index).map((q, idx) => ({
      ...q,
      question_number: idx + 1
    }));
    setAnswerKey(renumbered);
  };

  const saveAnswerKey = async () => {
    if (answerKey.length === 0) {
      toast.error('Please add at least one question');
      return;
    }

    // Validate
    for (const q of answerKey) {
      if (!q.correct_answer.trim()) {
        toast.error(`Question ${q.question_number}: Please enter correct answer`);
        return;
      }
      if (q.max_marks <= 0) {
        toast.error(`Question ${q.question_number}: Max marks must be greater than 0`);
        return;
      }
    }

    try {
      await axios.put(
        `${API}/marking/projects/${project_id}/answer-key`,
        { answer_key: answerKey },
        { withCredentials: true }
      );
      toast.success('Answer key saved successfully!');
      setEditingAnswerKey(false);
      fetchProject();
    } catch (error) {
      console.error('Error saving answer key:', error);
      toast.error('Failed to save answer key');
    }
  };

  // NEW: Handle answer key file upload
  const handleAnswerKeyUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only PDF, JPG, and PNG files are supported');
      return;
    }

    setUploadingAnswerKey(true);
    setAnswerKeyStatus('uploading');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API}/marking/projects/${project_id}/upload-answer-key`,
        formData,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      setExtractedAnswers(response.data.extracted_answers || []);
      setAnswerKeyStatus('pending_review');
      toast.success('Answer key extracted! Please review the answers below.');
      
    } catch (error) {
      console.error('Answer key upload/extraction failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to extract answer key');
      setAnswerKeyStatus('not_uploaded');
    } finally {
      setUploadingAnswerKey(false);
      // Reset file input
      e.target.value = '';
    }
  };

  // NEW: Confirm reviewed answer key
  const confirmAnswerKey = async () => {
    if (extractedAnswers.length === 0) {
      toast.error('No answers to confirm');
      return;
    }

    try {
      await axios.post(
        `${API}/marking/projects/${project_id}/confirm-answer-key`,
        { answer_key: extractedAnswers },
        { withCredentials: true }
      );
      
      setAnswerKey(extractedAnswers);
      setAnswerKeyStatus('confirmed');
      toast.success('Answer key confirmed! You can now upload student papers.');
      fetchProject();
      
    } catch (error) {
      console.error('Error confirming answer key:', error);
      toast.error('Failed to confirm answer key');
    }
  };

  // NEW: Update extracted answer during review
  const updateExtractedAnswer = (index, field, value) => {
    const updated = [...extractedAnswers];
    updated[index][field] = value;
    setExtractedAnswers(updated);
  };

  // NEW: Remove extracted answer
  const removeExtractedAnswer = (index) => {
    const updated = extractedAnswers.filter((_, i) => i !== index);
    // Renumber
    const renumbered = updated.map((q, idx) => ({
      ...q,
      question_number: idx + 1
    }));
    setExtractedAnswers(renumbered);
  };

  // NEW: Add manual answer to extracted list
  const addManualAnswer = () => {
    const newQuestion = {
      question_number: extractedAnswers.length + 1,
      question_text: '',
      correct_answer: '',
      accepted_alternatives: [],
      max_marks: 1.0,
      marking_guidance: '',
      confidence_score: 100,
      needs_review: false
    };
    setExtractedAnswers([...extractedAnswers, newQuestion]);
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length === 0) return;
    
    if (files.length > 50) {
      toast.error('Maximum 50 papers allowed per project');
      return;
    }

    if (!project.answer_key || project.answer_key.length === 0) {
      toast.error('Please add answer key before uploading student papers');
      return;
    }

    // Validate image quality for each file
    const validationPromises = files.map(file => validateImageQuality(file));
    const validations = await Promise.all(validationPromises);
    
    // Collect all issues
    const issues = [];
    validations.forEach((validation, index) => {
      if (validation.warnings.length > 0) {
        issues.push({
          fileName: files[index].name,
          warnings: validation.warnings,
          critical: !validation.valid
        });
      }
    });
    
    // Show quality modal if there are issues
    if (issues.length > 0) {
      setQualityIssues(issues);
      setPendingFiles(files);
      setShowQualityModal(true);
      return;
    }

    // No issues, proceed with upload
    await uploadFiles(files);
  };

  const uploadFiles = async (files) => {
    setUploading(true);
    let successCount = 0;

    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name}: File too large (max 10MB)`);
        continue;
      }

      const formData = new FormData();
      formData.append('file', file);

      try {
        await axios.post(
          `${API}/marking/projects/${project_id}/upload-paper?student_name=${file.name.split('.')[0]}`,
          formData,
          {
            withCredentials: true,
            headers: { 'Content-Type': 'multipart/form-data' }
          }
        );
        successCount++;
      } catch (error) {
        console.error(`Upload failed for ${file.name}:`, error);
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    setUploading(false);
    toast.success(`Uploaded ${successCount} paper(s) successfully!`);
    fetchProject();
  };

  const proceedWithUpload = () => {
    setShowQualityModal(false);
    uploadFiles(pendingFiles);
    setPendingFiles([]);
    setQualityIssues([]);
  };

  const cancelUpload = () => {
    setShowQualityModal(false);
    setPendingFiles([]);
    setQualityIssues([]);
    toast.info('Upload cancelled');
  };

  const handleZipUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!project.answer_key || project.answer_key.length === 0) {
      toast.error('Please add answer key before uploading student papers');
      return;
    }

    // Validate ZIP
    const validation = validateZipFile(file);
    if (!validation.valid) {
      validation.warnings.forEach(warning => toast.error(warning));
      return;
    }

    setUploading(true);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API}/marking/projects/${project_id}/upload-zip`,
        formData,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      toast.success(response.data.message);
      
      if (response.data.skipped_files.length > 0) {
        toast.warning(`Skipped ${response.data.skipped_files.length} file(s). Check console for details.`);
        console.warn('Skipped files:', response.data.skipped_files);
      }

      fetchProject();
    } catch (error) {
      console.error('ZIP upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload ZIP file');
    } finally {
      setUploading(false);
    }
  };

  const startProcessing = async () => {
    try {
      setProcessing(true);
      const response = await axios.post(
        `${API}/marking/projects/${project_id}/process`,
        {},
        { withCredentials: true }
      );
      toast.success(response.data.message);
      setActiveTab('papers');
      fetchProject();
    } catch (error) {
      console.error('Processing error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start processing');
    } finally {
      setProcessing(false);
    }
  };

  const batchApproveAll = async () => {
    try {
      const response = await axios.post(
        `${API}/marking/projects/${project_id}/batch-approve`,
        {},
        { withCredentials: true }
      );
      toast.success(response.data.message);
      fetchProject();
    } catch (error) {
      console.error('Batch approve error:', error);
      toast.error('Failed to batch approve submissions');
    }
  };

  const toggleSubmissionSelection = (submissionId) => {
    setSelectedSubmissions(prev => 
      prev.includes(submissionId)
        ? prev.filter(id => id !== submissionId)
        : [...prev, submissionId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedSubmissions.length === project.submissions?.length) {
      setSelectedSubmissions([]);
    } else {
      setSelectedSubmissions(project.submissions?.map(s => s.submission_id) || []);
    }
  };

  const batchDeleteSelected = async () => {
    if (selectedSubmissions.length === 0) {
      toast.error('No submissions selected');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedSubmissions.length} submission(s)? This action cannot be undone.`)) {
      return;
    }

    try {
      const queryParams = selectedSubmissions.map(id => `submission_ids=${id}`).join('&');
      const response = await axios.delete(
        `${API}/marking/projects/${project_id}/submissions/batch?${queryParams}`,
        { withCredentials: true }
      );
      toast.success(response.data.message);
      setSelectedSubmissions([]);
      fetchProject();
    } catch (error) {
      console.error('Batch delete error:', error);
      toast.error('Failed to delete submissions');
    }
  };

  const getConfidenceBadge = (confidence) => {
    if (confidence >= 80) {
      return { color: 'bg-green-100 text-green-700 border-green-300', label: 'High Confidence', icon: CheckCircle2 };
    } else if (confidence >= 60) {
      return { color: 'bg-amber-100 text-amber-700 border-amber-300', label: 'Review Recommended', icon: AlertCircle };
    } else {
      return { color: 'bg-red-100 text-red-700 border-red-300', label: 'Manual Review Required', icon: AlertCircle };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation />
        <div className="text-center py-20">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mx-auto mb-4"></div>
          <p className="text-lg text-slate-600">Loading project...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!project) return null;

  const pendingCount = project.submissions?.filter(s => s.processing_status === 'pending').length || 0;
  const processingCount = project.submissions?.filter(s => s.processing_status === 'processing').length || 0;
  const completedCount = project.submissions?.filter(s => s.processing_status === 'completed').length || 0;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />
      
      <section className="py-16 px-6 sm:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/assessment')}
              className="text-purple-600 hover:text-purple-700 font-medium mb-4 inline-flex items-center gap-2"
            >
              ← Back to Assessment Hub
            </button>
            
            <h1 className="text-3xl font-bold text-slate-900 mb-2">{project.project_name}</h1>
            <div className="flex items-center gap-4 text-sm text-slate-600">
              <span>📚 {project.subject || 'General'}</span>
              <span>🎓 Grade {project.grade_level}</span>
              <span>🌍 {project.curriculum}</span>
              <span>📝 {project.total_marks} marks</span>
            </div>
          </div>

          {/* Reminder Banner */}
          <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4 mb-8">
            <p className="text-sm text-blue-900 font-medium">
              📝 For classroom assessments only. Teacher review is required before finalising marks.
            </p>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-8 border-b-2 border-slate-100">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-6 py-3 font-semibold transition-all ${
                activeTab === 'overview'
                  ? 'text-purple-600 border-b-2 border-purple-600 -mb-0.5'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('answer-key')}
              className={`px-6 py-3 font-semibold transition-all ${
                activeTab === 'answer-key'
                  ? 'text-purple-600 border-b-2 border-purple-600 -mb-0.5'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Answer Key {answerKey.length > 0 && `(${answerKey.length})`}
            </button>
            <button
              onClick={() => setActiveTab('papers')}
              className={`px-6 py-3 font-semibold transition-all ${
                activeTab === 'papers'
                  ? 'text-purple-600 border-b-2 border-purple-600 -mb-0.5'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Student Papers {project.submissions && `(${project.submissions.length})`}
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <div>
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <FileText className="w-8 h-8 text-blue-600" />
                    <span className="text-3xl font-bold text-slate-900">{answerKey.length}</span>
                  </div>
                  <h3 className="text-base font-semibold text-slate-900 mb-1">Questions</h3>
                  <p className="text-sm text-slate-600">In answer key</p>
                </div>

                <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <Upload className="w-8 h-8 text-purple-600" />
                    <span className="text-3xl font-bold text-slate-900">{project.submissions?.length || 0}</span>
                  </div>
                  <h3 className="text-base font-semibold text-slate-900 mb-1">Papers Uploaded</h3>
                  <p className="text-sm text-slate-600">Maximum 50 allowed</p>
                </div>

                <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <CheckCircle2 className="w-8 h-8 text-green-600" />
                    <span className="text-3xl font-bold text-slate-900">{completedCount}</span>
                  </div>
                  <h3 className="text-base font-semibold text-slate-900 mb-1">Marked</h3>
                  <p className="text-sm text-slate-600">Ready for review</p>
                </div>
              </div>

              {/* Reports & Analytics Section */}
              {completedCount > 0 && (
                <div className="grid md:grid-cols-2 gap-6">
                  <button
                    onClick={() => navigate(`/assessment/analytics/${project_id}`)}
                    className="bg-gradient-to-br from-blue-500 to-indigo-500 rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-all group text-left"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                        <BarChart3 className="w-6 h-6" />
                      </div>
                      <span className="text-sm bg-white/20 px-3 py-1 rounded-full">{completedCount} approved</span>
                    </div>
                    <h3 className="text-xl font-bold mb-2">View Class Analytics</h3>
                    <p className="text-blue-100 text-sm">
                      Question-wise performance, grade distribution, and common errors
                    </p>
                  </button>

                  <div className="bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl p-6 text-white shadow-lg">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                        <FileBarChart className="w-6 h-6" />
                      </div>
                    </div>
                    <h3 className="text-xl font-bold mb-2">Individual Reports</h3>
                    <p className="text-purple-100 text-sm mb-3">
                      View detailed reports for each student from the papers list below
                    </p>
                    <button
                      onClick={() => setActiveTab('papers')}
                      className="text-sm bg-white text-purple-600 hover:bg-purple-50 px-4 py-2 rounded-lg font-semibold transition-all"
                    >
                      Go to Student Papers →
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'answer-key' && (
            <div className="bg-white rounded-2xl border border-slate-100 p-8 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-slate-900">Answer Key</h2>
                {answerKeyStatus === 'confirmed' && (
                  <span className="inline-flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded-lg font-semibold">
                    <CheckCircle2 className="w-5 h-5" />
                    Confirmed
                  </span>
                )}
              </div>

              {/* Upload Answer Key Section - Show only if no answer key exists */}
              {(answerKeyStatus === 'not_uploaded' || answerKeyStatus === 'uploading') && answerKey.length === 0 && (
                <div>
                  <label 
                    htmlFor="answer-key-upload" 
                    className="block border-2 border-dashed border-purple-300 rounded-xl p-8 text-center hover:border-purple-400 hover:bg-purple-50 transition-all cursor-pointer bg-purple-50/30"
                    style={{ pointerEvents: uploadingAnswerKey ? 'none' : 'auto', opacity: uploadingAnswerKey ? 0.6 : 1 }}
                  >
                    <input
                      type="file"
                      id="answer-key-upload"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={handleAnswerKeyUpload}
                      className="hidden"
                      disabled={uploadingAnswerKey}
                    />
                    {uploadingAnswerKey ? (
                      <>
                        <Loader2 className="w-12 h-12 text-purple-600 mx-auto mb-4 animate-spin" />
                        <p className="text-base font-semibold text-slate-900 mb-2">
                          {answerKeyStatus === 'uploading' ? 'Uploading...' : 'Extracting answers with AI...'}
                        </p>
                      </>
                    ) : (
                      <>
                        <Upload className="w-12 h-12 text-purple-600 mx-auto mb-4" />
                        <p className="text-base font-semibold text-slate-900 mb-2">
                          Upload Answer Key or Teacher-Corrected Paper
                        </p>
                      </>
                    )}
                    <p className="text-sm text-slate-600">
                      Upload a typed answer key, handwritten answer key, or teacher-corrected paper.<br />
                      AI will extract the correct answers for you.
                    </p>
                    <p className="text-xs text-slate-500 mt-2">
                      PDF, JPG, or PNG • Max 10MB per file
                    </p>
                  </label>

                  <div className="mt-6 flex items-center gap-4">
                    <div className="flex-1 h-px bg-slate-200"></div>
                    <span className="text-sm text-slate-500 font-medium">OR</span>
                    <div className="flex-1 h-px bg-slate-200"></div>
                  </div>

                  <div className="mt-6 text-center">
                    <button
                      onClick={() => { addQuestion(); setEditingAnswerKey(true); setAnswerKeyStatus('manual_entry'); }}
                      className="bg-slate-100 text-slate-700 hover:bg-slate-200 px-6 py-3 rounded-xl font-semibold transition-all inline-flex items-center gap-2"
                    >
                      <Plus className="w-5 h-5" />
                      Enter Answer Key Manually
                    </button>
                  </div>
                </div>
              )}

              {/* Review Extracted Answers */}
              {answerKeyStatus === 'pending_review' && (
                <div className="space-y-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div>
                        <h3 className="font-semibold text-blue-900">Review Extracted Answers</h3>
                        <p className="text-sm text-blue-700 mt-1">
                          AI has extracted {extractedAnswers.length} question{extractedAnswers.length !== 1 ? 's' : ''} from your answer key. 
                          Please review and edit if needed, then confirm to start marking.
                        </p>
                      </div>
                    </div>
                  </div>

                  {extractedAnswers.map((q, index) => (
                    <div 
                      key={index} 
                      className={`border-2 rounded-xl p-6 ${q.needs_review || q.confidence_score < 80 ? 'border-yellow-300 bg-yellow-50' : 'border-slate-200 bg-slate-50'}`}
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <h3 className="text-lg font-bold text-slate-900">Question {q.question_number}</h3>
                          {(q.needs_review || q.confidence_score < 80) && (
                            <span className="inline-flex items-center gap-1 bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-semibold">
                              <AlertTriangle className="w-3 h-3" />
                              Needs Review
                            </span>
                          )}
                          {q.confidence_score && (
                            <span className="text-xs text-slate-500">
                              Confidence: {q.confidence_score}%
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => removeExtractedAnswer(index)}
                          className="text-red-600 hover:text-red-700 p-2 hover:bg-red-50 rounded-lg transition-all"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-semibold text-slate-700 mb-2">Question Text</label>
                          <input
                            type="text"
                            value={q.question_text || ''}
                            onChange={(e) => updateExtractedAnswer(index, 'question_text', e.target.value)}
                            placeholder="Question text (optional)"
                            className="w-full rounded-lg border-2 border-slate-200 px-3 py-2 text-sm"
                          />
                        </div>

                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">Correct Answer *</label>
                            <input
                              type="text"
                              value={q.correct_answer}
                              onChange={(e) => updateExtractedAnswer(index, 'correct_answer', e.target.value)}
                              placeholder="Correct answer"
                              className="w-full rounded-lg border-2 border-slate-200 px-3 py-2 text-sm"
                              required
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">Max Marks *</label>
                            <input
                              type="number"
                              value={q.max_marks}
                              onChange={(e) => updateExtractedAnswer(index, 'max_marks', parseFloat(e.target.value))}
                              min="0.5"
                              step="0.5"
                              className="w-full rounded-lg border-2 border-slate-200 px-3 py-2 text-sm"
                              required
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-slate-700 mb-2">Accepted Alternatives (comma-separated)</label>
                          <input
                            type="text"
                            value={(q.accepted_alternatives || []).join(', ')}
                            onChange={(e) => updateExtractedAnswer(index, 'accepted_alternatives', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                            placeholder="e.g., four, 4.0"
                            className="w-full rounded-lg border-2 border-slate-200 px-3 py-2 text-sm"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-slate-700 mb-2">Marking Guidance</label>
                          <textarea
                            value={q.marking_guidance || ''}
                            onChange={(e) => updateExtractedAnswer(index, 'marking_guidance', e.target.value)}
                            placeholder="e.g., Award partial marks for correct method"
                            rows={2}
                            className="w-full rounded-lg border-2 border-slate-200 px-3 py-2 text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  ))}

                  <div className="flex gap-4">
                    <button
                      onClick={addManualAnswer}
                      className="bg-slate-100 text-slate-700 hover:bg-slate-200 px-6 py-3 rounded-xl font-semibold transition-all inline-flex items-center gap-2"
                    >
                      <Plus className="w-5 h-5" />
                      Add Missing Question
                    </button>

                    <button
                      onClick={confirmAnswerKey}
                      className="flex-1 bg-purple-600 text-white hover:bg-purple-700 px-6 py-3 rounded-xl font-semibold transition-all inline-flex items-center justify-center gap-2"
                    >
                      <CheckCircle2 className="w-5 h-5" />
                      Confirm Answer Key
                    </button>
                  </div>
                </div>
              )}

              {/* Confirmed Answer Key Display */}
              {(answerKeyStatus === 'confirmed' || (answerKeyStatus === 'not_uploaded' && answerKey.length > 0)) && answerKey.length > 0 && (
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
                      <div>
                        <h3 className="font-semibold text-green-900">Answer Key Confirmed</h3>
                        <p className="text-sm text-green-700 mt-1">
                          {answerKey.length} question{answerKey.length !== 1 ? 's' : ''} ready for marking. You can now upload student papers.
                        </p>
                      </div>
                    </div>
                  </div>

                  {answerKey.map((q) => (
                    <div key={q.question_number} className="border border-slate-200 rounded-xl p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-bold text-slate-900 mb-1">Q{q.question_number}: {q.question_text || 'Question'}</h3>
                          <p className="text-sm text-slate-600"><strong>Correct Answer:</strong> {q.correct_answer}</p>
                          {q.accepted_alternatives && q.accepted_alternatives.length > 0 && (
                            <p className="text-sm text-slate-600"><strong>Alternatives:</strong> {q.accepted_alternatives.join(', ')}</p>
                          )}
                          <p className="text-sm text-slate-600"><strong>Marks:</strong> {q.max_marks}</p>
                          {q.marking_guidance && (
                            <p className="text-sm text-slate-500 mt-1"><strong>Guidance:</strong> {q.marking_guidance}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}

                  <button
                    onClick={() => setAnswerKeyStatus('not_uploaded')}
                    className="text-red-600 hover:text-red-700 font-semibold text-sm"
                  >
                    Replace Answer Key
                  </button>
                </div>
              )}

              {/* Manual Entry Mode */}
              {answerKeyStatus === 'manual_entry' && editingAnswerKey && (
                <div className="space-y-6">
                  {answerKey.map((q, index) => (
                    <div key={index} className="border border-slate-200 rounded-xl p-6 bg-slate-50">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-bold text-slate-900">Question {q.question_number}</h3>
                        <button
                          onClick={() => removeQuestion(index)}
                          className="text-red-600 hover:text-red-700 p-2 hover:bg-red-50 rounded-lg transition-all"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-semibold text-slate-700 mb-2">Question Text (Optional)</label>
                          <input
                            type="text"
                            value={q.question_text}
                            onChange={(e) => updateQuestion(index, 'question_text', e.target.value)}
                            placeholder="e.g., What is 2 + 2?"
                            className="w-full rounded-lg border-2 border-slate-200 px-3 py-2 text-sm"
                          />
                        </div>

                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">Correct Answer *</label>
                            <input
                              type="text"
                              value={q.correct_answer}
                              onChange={(e) => updateQuestion(index, 'correct_answer', e.target.value)}
                              placeholder="e.g., 4"
                              className="w-full rounded-lg border-2 border-slate-200 px-3 py-2 text-sm"
                              required
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">Max Marks *</label>
                            <input
                              type="number"
                              value={q.max_marks}
                              onChange={(e) => updateQuestion(index, 'max_marks', parseFloat(e.target.value))}
                              min="0.5"
                              step="0.5"
                              className="w-full rounded-lg border-2 border-slate-200 px-3 py-2 text-sm"
                              required
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-slate-700 mb-2">Marking Instructions</label>
                          <textarea
                            value={q.marking_instructions || ''}
                            onChange={(e) => updateQuestion(index, 'marking_instructions', e.target.value)}
                            placeholder="e.g., Award partial marks for correct method"
                            rows={2}
                            className="w-full rounded-lg border-2 border-slate-200 px-3 py-2 text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  ))}

                  <div className="flex gap-4">
                    <button
                      onClick={addQuestion}
                      className="bg-slate-100 text-slate-700 hover:bg-slate-200 px-6 py-3 rounded-xl font-semibold transition-all inline-flex items-center gap-2"
                    >
                      <Plus className="w-5 h-5" />
                      Add Question
                    </button>

                    <button
                      onClick={saveAnswerKey}
                      className="bg-purple-600 text-white hover:bg-purple-700 px-6 py-3 rounded-xl font-semibold transition-all"
                    >
                      Save Answer Key
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'papers' && (
            <div className="space-y-6">
              <div className="bg-white rounded-2xl border border-slate-100 p-8 shadow-sm">
                <h2 className="text-2xl font-bold text-slate-900 mb-4">Upload Student Papers</h2>
                
                <label 
                  htmlFor="paper-upload" 
                  className="block border-2 border-dashed border-slate-300 rounded-xl p-8 text-center hover:border-purple-400 hover:bg-purple-50 transition-all cursor-pointer"
                  style={{ pointerEvents: (!project.answer_key || project.answer_key.length === 0 || uploading) ? 'none' : 'auto', opacity: (!project.answer_key || project.answer_key.length === 0 || uploading) ? 0.5 : 1 }}
                >
                  <input
                    type="file"
                    id="paper-upload"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleFileUpload}
                    className="hidden"
                    disabled={uploading || !project.answer_key || project.answer_key.length === 0}
                  />
                  <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                  <p className="text-base font-semibold text-slate-900 mb-2">
                    {uploading ? 'Uploading...' : 'Click to upload student papers'}
                  </p>
                  <p className="text-sm text-slate-600">
                    PDF, JPG, or PNG • Max 10MB per file • Up to 50 papers
                  </p>
                  {(!project.answer_key || project.answer_key.length === 0) && (
                    <p className="text-sm text-red-600 mt-2">⚠️ Add answer key first</p>
                  )}
                </label>

                <div className="mt-6 flex items-center gap-4">
                  <div className="flex-1 h-px bg-slate-200"></div>
                  <span className="text-sm text-slate-500 font-medium">OR</span>
                  <div className="flex-1 h-px bg-slate-200"></div>
                </div>

                <label 
                  htmlFor="zip-upload" 
                  className="mt-6 block border-2 border-dashed border-blue-300 rounded-xl p-6 text-center hover:border-blue-400 hover:bg-blue-50 transition-all cursor-pointer bg-blue-50/30"
                  style={{ pointerEvents: (!project.answer_key || project.answer_key.length === 0 || uploading) ? 'none' : 'auto', opacity: (!project.answer_key || project.answer_key.length === 0 || uploading) ? 0.5 : 1 }}
                >
                  <input
                    type="file"
                    id="zip-upload"
                    accept=".zip"
                    onChange={handleZipUpload}
                    className="hidden"
                    disabled={uploading || !project.answer_key || project.answer_key.length === 0}
                  />
                  <FileArchive className="w-10 h-10 text-blue-600 mx-auto mb-3" />
                  <p className="text-base font-semibold text-slate-900 mb-1">
                    Upload ZIP File (Bulk Upload)
                  </p>
                  <p className="text-sm text-slate-600">
                    Upload multiple papers at once • Max 100MB ZIP • Up to 50 files inside
                  </p>
                  {(!project.answer_key || project.answer_key.length === 0) && (
                    <p className="text-sm text-red-600 mt-2">⚠️ Add answer key first</p>
                  )}
                </label>

                {pendingCount > 0 && (
                  <div className="mt-6">
                    <button
                      onClick={startProcessing}
                      disabled={processing}
                      className="w-full bg-purple-600 text-white hover:bg-purple-700 px-6 py-4 rounded-xl font-semibold transition-all inline-flex items-center justify-center gap-2 disabled:bg-slate-300"
                    >
                      {processing ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Starting AI Processing...
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="w-5 h-5" />
                          Start AI Marking ({pendingCount} paper{pendingCount !== 1 ? 's' : ''})
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>

              {/* Processing Status */}
              {(processingCount > 0 || completedCount > 0) && (
                <div className="bg-white rounded-2xl border border-slate-100 p-8 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-4">
                      <h3 className="text-xl font-bold text-slate-900">Processing Status</h3>
                      {project.submissions?.length > 0 && (
                        <button
                          onClick={toggleSelectAll}
                          className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                        >
                          {selectedSubmissions.length === project.submissions?.length ? 'Deselect All' : 'Select All'}
                        </button>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-3">
                      {selectedSubmissions.length > 0 && (
                        <button
                          onClick={batchDeleteSelected}
                          className="bg-red-600 text-white hover:bg-red-700 px-4 py-2 rounded-lg font-semibold text-sm transition-all inline-flex items-center gap-2"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete Selected ({selectedSubmissions.length})
                        </button>
                      )}
                      
                      {project.submissions?.some(s => s.processing_status === 'completed' && s.teacher_reviewed && !s.teacher_approved) && (
                        <button
                          onClick={batchApproveAll}
                          className="bg-green-600 text-white hover:bg-green-700 px-4 py-2 rounded-lg font-semibold text-sm transition-all inline-flex items-center gap-2"
                        >
                          <CheckCircle2 className="w-4 h-4" />
                          Approve All Reviewed
                        </button>
                      )}
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    {project.submissions?.map((sub) => (
                      <div key={sub.submission_id} className="flex items-center justify-between border border-slate-200 rounded-lg p-4">
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={selectedSubmissions.includes(sub.submission_id)}
                            onChange={() => toggleSubmissionSelection(sub.submission_id)}
                            className="w-5 h-5 rounded border-2 border-slate-300 text-purple-600 focus:ring-2 focus:ring-purple-100 cursor-pointer"
                          />
                          
                          {sub.processing_status === 'completed' ? (
                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                          ) : sub.processing_status === 'processing' ? (
                            <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                          ) : sub.processing_status === 'failed' ? (
                            <AlertCircle className="w-5 h-5 text-red-600" />
                          ) : (
                            <Clock className="w-5 h-5 text-slate-400" />
                          )}
                          <span className="font-semibold text-slate-900">{sub.student_name}</span>
                        </div>
                        
                        {sub.processing_status === 'completed' && (
                          <div className="flex items-center gap-3">
                            {sub.teacher_approved && (
                              <button
                                onClick={() => navigate(`/assessment/report/${sub.submission_id}`)}
                                className="text-blue-600 hover:text-blue-700 font-semibold text-sm"
                              >
                                View Report
                              </button>
                            )}
                            <button
                              onClick={() => navigate(`/assessment/review/${sub.submission_id}`)}
                              className="text-purple-600 hover:text-purple-700 font-semibold"
                            >
                              {sub.teacher_approved ? 'View/Edit' : 'Review Marks'} →
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
      
      {/* Quality Warning Modal */}
      {showQualityModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[80vh] overflow-auto shadow-2xl">
            <div className="p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">Image Quality Warnings</h2>
                  <p className="text-base text-slate-600">
                    Some images have quality issues that may affect OCR accuracy. Review the warnings below.
                  </p>
                </div>
              </div>

              <div className="space-y-4 mb-6">
                {qualityIssues.map((issue, index) => (
                  <div key={index} className={`border-2 rounded-xl p-4 ${issue.critical ? 'border-red-300 bg-red-50' : 'border-amber-300 bg-amber-50'}`}>
                    <h3 className="font-bold text-slate-900 mb-2 flex items-center gap-2">
                      {issue.critical ? <AlertCircle className="w-5 h-5 text-red-600" /> : <AlertTriangle className="w-5 h-5 text-amber-600" />}
                      {issue.fileName}
                    </h3>
                    <ul className="space-y-1">
                      {issue.warnings.map((warning, wIndex) => (
                        <li key={wIndex} className="text-sm text-slate-700 flex items-start gap-2">
                          <span className="text-amber-600 font-bold mt-0.5">•</span>
                          <span>{warning}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                <p className="text-sm text-blue-900 font-medium mb-2">💡 Recommendations for better results:</p>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Use good lighting when taking photos</li>
                  <li>• Hold camera steady to avoid blur</li>
                  <li>• Ensure text is clearly visible</li>
                  <li>• Use landscape orientation for full pages</li>
                  <li>• Keep image resolution between 600x600 and 4000x4000</li>
                </ul>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={cancelUpload}
                  className="flex-1 bg-slate-100 text-slate-700 hover:bg-slate-200 px-6 py-3 rounded-xl font-semibold transition-all"
                >
                  Cancel & Re-upload
                </button>
                <button
                  onClick={proceedWithUpload}
                  className="flex-1 bg-purple-600 text-white hover:bg-purple-700 px-6 py-3 rounded-xl font-semibold transition-all"
                >
                  Proceed Anyway ({pendingFiles.length} file{pendingFiles.length !== 1 ? 's' : ''})
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <Footer />
    </div>
  );
}

export default MarkingProjectDetail;
